import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, X, Zap, Crown, Rocket } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PlanIntentModal from '../components/PlanIntentModal';
import { analytics } from '../lib/analytics';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // `null` = ainda nao sabemos. Ate saber, o botao nao promete pagamento.
  const [billing, setBilling] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Quem manda no botao e o servidor: sem chave do Stripe configurada, o ecra
  // mantem a lista de espera que ja existia em vez de abrir um checkout que
  // ia falhar.
  useEffect(() => {
    let cancelado = false;
    axios
      .get(`${BACKEND_URL}/api/billing/config`)
      .then((r) => { if (!cancelado) setBilling(r.data); })
      .catch(() => { if (!cancelado) setBilling({ enabled: false, plans: {} }); });
    return () => { cancelado = true; };
  }, []);

  const plans = [
    {
      name: 'Basic',
      icon: Zap,
      price: { monthly: 29, annual: 290 },
      description: 'Perfect for getting started with arbitrage trading',
      features: [
        { text: 'Access to 100 arbitrage opportunities daily', included: true },
        { text: '5-minute data refresh rate', included: true },
        { text: 'Basic AI rule verification', included: true },
        { text: 'Telegram channel alerts', included: true },
        { text: 'AI chat + Telegram support', included: true },
        { text: 'Real-time orderbook data', included: false },
        { text: 'Telegram bot access', included: false },
        { text: 'Priority support', included: false }
      ],
      popular: false,
      cta: 'Get Started'
    },
    {
      name: 'Pro',
      icon: Crown,
      price: { monthly: 99, annual: 990 },
      description: 'Advanced features for serious arbitrage traders',
      features: [
        { text: 'Unlimited arbitrage opportunities', included: true },
        { text: '1-minute data refresh rate', included: true },
        { text: 'Advanced AI rule verification', included: true },
        { text: 'Telegram alerts + bot access', included: true },
        { text: 'Real-time orderbook data', included: true },
        { text: 'Position tracking dashboard', included: true },
        { text: 'Priority Telegram support', included: true },
        { text: 'API access', included: false }
      ],
      popular: true,
      cta: 'Get Started'
    },
    {
      name: 'Advanced',
      icon: Rocket,
      price: { monthly: 299, annual: 2990 },
      description: 'Maximum power for professional arbitrage operations',
      features: [
        { text: 'Everything in Pro, plus:', included: true },
        { text: 'Real-time streaming (sub-second)', included: true },
        { text: 'Full API access', included: true },
        { text: 'Custom alert webhooks', included: true },
        { text: 'Historical data access', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: '24/7 priority support', included: true },
        { text: 'Custom integrations', included: true }
      ],
      popular: false,
      cta: 'Get Started'
    }
  ];

  const getPrice = (plan) => {
    const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual;
    const period = billingCycle === 'monthly' ? 'mo' : 'yr';
    return { price, period };
  };

  const getSavings = (plan) => {
    const monthlyCost = plan.price.monthly * 12;
    const annualCost = plan.price.annual;
    const savings = monthlyCost - annualCost;
    return Math.round((savings / monthlyCost) * 100);
  };

  const planKey = (plan) => plan.name.toLowerCase();

  const checkoutAvailable = (plan) =>
    Boolean(billing?.enabled && billing?.plans?.[planKey(plan)]?.[billingCycle]);

  const handlePlanClick = async (plan) => {
    analytics.ctaClick(`pricing_${planKey(plan)}`);
    setCheckoutError('');

    if (!checkoutAvailable(plan)) {
      setSelectedPlan(plan);
      setIsModalOpen(true);
      return;
    }

    // Pagar exige conta: e a ela que a subscricao fica presa.
    if (!user) {
      navigate(`/register?plan=${planKey(plan)}&cycle=${billingCycle}`);
      return;
    }

    setBusyPlan(plan.name);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/billing/checkout`,
        { plan: planKey(plan), cycle: billingCycle },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (error) {
      setBusyPlan(null);
      setCheckoutError(
        error.response?.data?.detail || 'Could not start checkout. Please try again.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#06070d] via-[#0b0e1a] to-[#06070d]">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-white/5 px-4 py-2 rounded-lg border border-white/10 mb-6">
              <span className="text-[#f2e2a6] text-sm font-mono uppercase tracking-wider">Pricing Plans</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Choose Your <span className="text-[#f2e2a6]">Plan</span>
            </h1>
            <p className="text-gray-400 text-xl max-w-2xl mx-auto">
              Start finding profitable arbitrage opportunities across prediction markets today
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white/5 rounded-lg p-1 border border-white/10">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-[#f2e2a6] text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="billing-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-8 py-3 rounded-lg font-semibold transition-all relative ${
                  billingCycle === 'annual'
                    ? 'bg-[#f2e2a6] text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="billing-annual"
              >
                Annual
                <span className="absolute -top-2 -right-2 bg-[#f2e2a6] text-black text-xs px-2 py-0.5 rounded-full font-bold">
                  Save 17%
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const { price, period } = getPrice(plan);
              const Icon = plan.icon;
              
              return (
                <div
                  key={plan.name}
                  className={`relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 transition-all hover:scale-105 ${
                    plan.popular
                      ? 'border-2 border-[#f2e2a6] shadow-2xl shadow-[#f2e2a6]/20'
                      : 'border border-white/10'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`plan-card-${plan.name.toLowerCase()}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-[#f2e2a6] text-black px-4 py-1 rounded-full text-sm font-bold">
                        MOST POPULAR
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="w-12 h-12 bg-[#f2e2a6]/20 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#f2e2a6]" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-400 text-sm">{plan.description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold text-white">${price}</span>
                      <span className="text-gray-400 ml-2">/{period}</span>
                    </div>
                    {billingCycle === 'annual' && (
                      <div className="text-[#f2e2a6] text-sm mt-2">
                        Save {getSavings(plan)}% with annual billing
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => handlePlanClick(plan)}
                    disabled={busyPlan === plan.name}
                    data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                    className={`w-full py-6 mb-6 font-semibold text-lg transition-all disabled:opacity-60 ${
                      plan.popular
                        ? 'bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {busyPlan === plan.name
                      ? 'Redirecting…'
                      : checkoutAvailable(plan)
                        ? 'Subscribe'
                        : plan.cta}
                  </Button>

                  {checkoutError && busyPlan === null && (
                    <p className="text-red-400 text-sm mb-4" data-testid="checkout-error">
                      {checkoutError}
                    </p>
                  )}

                  <div className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-[#f2e2a6] flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={feature.included ? 'text-gray-300' : 'text-gray-600'}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* FAQ Section */}
          <div className="mt-20 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Questions?</h2>
            <p className="text-gray-400 mb-6">Check out our <a href="/#faq" className="text-[#f2e2a6] hover:underline">FAQ section</a> or contact support</p>
            <div className="flex justify-center space-x-4">
              <Button className="bg-white/10 text-white hover:bg-white/20">
                Contact Support
              </Button>
              <a href="/">
                <Button className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90">
                  Back to Home
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Plan Intent Modal */}
      <PlanIntentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        planName={selectedPlan?.name || ''}
        planPrice={selectedPlan ? getPrice(selectedPlan).price : 0}
      />
    </div>
  );
};

export default Pricing;
